import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbCommonModule } from 'app/abb-common.module';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  Subscription,
} from 'rxjs';
import {
  Coordinates,
  CreateUserMutation,
  CreateUserMutationVariables,
  PropertyDetailContactsQuery,
  PropertyDetailsQuery,
  User,
  Roles,
  GetCountryDetailsQuery,
  PropertyDevicesQuery,
  CanCreateUserQuery,
  CanCreateUserQueryVariables,
} from 'gql/graphql';
import { getLicenseIdQuery } from '@graphql/account';
import { propertyLicenseTypeQuery } from '@graphql/property';
import { Apollo } from 'apollo-angular';
import {
  propertyDetailContactsQuery,
  propertyDeviceQuery,
} from '@graphql/property';
import { AbbStepperComponent } from '@components/abb-stepper/abb-stepper.component';
import { ROLES } from '@constants/user';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { canCreateUserQuery, createUserMutation } from '@graphql/user';
import { AbbAddressFieldComponent } from '@components/abb-form/abb-address-field.component';
import { CountryDetailsService } from '@services/county-details/country-details.service';
import { t } from '@utils/strings';

type PartialUser = Pick<User, 'id' | 'name' | 'lastName' | 'email'>;

@Component({
  selector: 'abb-property-form',
  standalone: true,
  host: {
    class: 'flex flex-col h-full min-h-0',
  },
  imports: [
    TPipe,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    AbbCommonModule,
    MatAutocompleteModule,
    MatIconModule,
    MatSelectModule,
    AbbStepperComponent,
    AbbAddressFieldComponent,
  ],
  template: `
    @if (stepperView()) {
    <abb-stepper
      class="w-full"
      [stepsControls]="stepsControls()"
      [stepsLabels]="stepsLabels()"
      [showStepLabelsInDots]="false"
      [isVertical]="true"
      [isLoading]="isLoading()"
      [stepperTitle]="(mode() === 'new' ? 'New Property' : 'Edit Property') | t"
      (onCancel)="cancel()"
      (onValidationError)="showError($event)"
    >
      <ng-template #stepContent let-index="index">
        <form
          id="property-form"
          class="flex flex-row gap-3"
          [formGroup]="form"
          (ngSubmit)="onSubmit()"
        >
          @if(index === 0) {
          <div
            [formGroup]="form.controls.propertyInfo"
            formGroupName="propertyInfo"
            class="flex flex-row w-full gap-3"
          >
            <div class="flex flex-col w-full gap-3">
              <mat-form-field class="w-full" color="primary">
                <mat-label>{{ 'Name' | t }}</mat-label>
                <input matInput id="name" formControlName="name" name="name" />
                @if(form.controls.propertyInfo.get('name')?.hasError('required')){
                <mat-error>{{ 'This field is required' | t }}</mat-error>
                }
              </mat-form-field>
              @if(mode() === 'edit' && licenseId() && isLTLicense()) {
              <mat-form-field class="w-full" color="primary">
                <mat-label>{{ 'Heated Floor Area' | t }}</mat-label>
                <input
                  matInput
                  type="number"
                  formControlName="heatedFloorArea"
                  name="heatedFloorArea"
                />
                @if(form.controls.propertyInfo.get('heatedFloorArea')?.hasError('min')){
                <mat-error>{{ 'Value must be positive' | t }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field class="w-full" color="primary">
                <mat-label class="w-full text-xs">{{
                  'Heating System Type' | t
                }}</mat-label>
                <mat-select
                  formControlName="heatingSystemType"
                  name="heatingSystemType"
                >
                  <mat-option value="electricity">{{
                    'Electricity' | t
                  }}</mat-option>
                  <mat-option value="natural gas">{{
                    'Natural Gas' | t
                  }}</mat-option>
                  <mat-option value="district heating">{{
                    'District Heating' | t
                  }}</mat-option>
                  <mat-option value="oil">{{ 'Oil' | t }}</mat-option>
                  <mat-option value="wood">{{ 'Wood' | t }}</mat-option>
                  <mat-option value="heat pump air">{{
                    'Heat Pump Air' | t
                  }}</mat-option>
                  <mat-option value="heat pump earth">{{
                    'Heat Pump Earth' | t
                  }}</mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field class="w-full" color="primary">
                <mat-label>{{ 'Category' | t }}</mat-label>
                <mat-select formControlName="category" name="category">
                  <mat-option value="healthcare">{{
                    'Healthcare' | t
                  }}</mat-option>
                  <mat-option value="industrial">{{
                    'Industrial' | t
                  }}</mat-option>
                  <mat-option value="hotel">{{ 'Hotel' | t }}</mat-option>
                  <mat-option value="office">{{ 'Office' | t }}</mat-option>
                  <mat-option value="office/residential">{{
                    'Office/Residential' | t
                  }}</mat-option>
                  <mat-option value="other">{{ 'Other' | t }}</mat-option>
                  <mat-option value="residential">{{
                    'Residential' | t
                  }}</mat-option>
                  <mat-option value="retail">{{ 'Retail' | t }}</mat-option>
                  <mat-option value="retail/office">{{
                    'Retail/office' | t
                  }}</mat-option>
                  <mat-option value="warehouse">{{
                    'Warehouse' | t
                  }}</mat-option>
                </mat-select>
              </mat-form-field>

              }
              <mat-form-field class="w-full" color="primary">
                <mat-label>{{ 'Timezone' | t }}</mat-label>
                <mat-select
                  formControlName="timezone"
                  id="timezone"
                  name="timezone"
                  [panelClass]="'panel'"
                >
                  @if(countryDetails() | async; as countryDetails){ @for
                  (timezone of countryDetails.time_zones; track timezone?.id) {
                  <mat-option [id]="timezone?.id ?? ''" [value]="timezone?.id">
                    {{ timezone?.zone }}
                    <small>({{ timezone?.utc_offset }})</small>
                  </mat-option>
                  }}
                </mat-select>
                @if(!this.showAddressHint()){
                <mat-hint class="text-abb-font/65"
                  ><small>{{ 'Choose an address first' | t }}</small></mat-hint
                >
                }
                @if(form.controls.propertyInfo.get('timezone')?.hasError('required')){
                <mat-error>{{ 'This field is required' | t }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field class="w-full" color="primary">
                <mat-label>{{ 'Currency' | t }}</mat-label>
                <mat-select
                  formControlName="currency"
                  id="currency"
                  name="currency"
                  [panelClass]="'panel'"
                >
                  @if(countryDetails() | async; as countryDetails){ @for
                  (currency of countryDetails.currencies; track currency?.id) {
                  <mat-option [id]="currency?.id ?? ''" [value]="currency?.id">
                    {{ currency?.value }}
                  </mat-option>
                  }}
                </mat-select>
                @if(!this.showAddressHint()){
                <mat-hint class="text-abb-font/65"
                  ><small>{{ 'Choose an address first' | t }}</small></mat-hint
                >
                }
                @if(form.controls.propertyInfo.get('currency')?.hasError('required')){
                <mat-error>{{ 'This field is required' | t }}</mat-error>
                }
              </mat-form-field>
              <div class="image-upload-container">
                @if(imagePreview){
                <div class="image-preview">
                  <img
                    [src]="imagePreview"
                    alt="Image Preview"
                    class="preview-image"
                  />
                </div>
                }

                <label for="imageUpload" class="flex image-upload-label"
                  ><abb-icon
                    class="mr-2"
                    [name]="'mat/image'"
                    [externalSize]="'text-lg'"
                  ></abb-icon
                  >{{ 'Choose an Image' | t }}</label
                >
                <input
                  type="file"
                  id="imageUpload"
                  (change)="onFileSelected($event)"
                  accept="image/*"
                />
              </div>
            </div>

            <div class="flex flex-col w-full gap-3">
              @if(mode() === 'edit' && licenseId() && isLTLicense()) {
              <mat-form-field class="w-full" color="primary">
                <mat-label class="w-full text-xs">{{
                  'Heating Energy Consumption' | t
                }}</mat-label>
                <input
                  matInput
                  type="number"
                  formControlName="heatingEnergyConsumption"
                  name="heatingEnergyConsumption"
                />
                @if(form.controls.propertyInfo.get('heatingEnergyConsumption')?.hasError('min')){
                <mat-error>{{ 'Value must be positive' | t }}</mat-error>
                }</mat-form-field
              >
              <mat-form-field class="w-full" color="primary">
                <mat-label class="w-full text-xs">{{
                  'Electrical Energy Consumption' | t
                }}</mat-label>
                <input
                  matInput
                  type="number"
                  formControlName="electricalEnergyConsumption"
                  name="electricalEnergyConsumption"
                />
                @if(form.controls.propertyInfo.get('electricalEnergyConsumption')?.hasError('min')){
                <mat-error>{{ 'Value must be positive' | t }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field class="w-full" color="primary">
                <mat-label>{{ 'Construction Year' | t }}</mat-label>
                <input
                  matInput
                  type="number"
                  formControlName="constructionYear"
                  name="constructionYear"
                />
                @if(form.controls.propertyInfo.get('constructionYear')?.hasError('yearFormat')){
                <mat-error>{{
                  'Please enter a valid year (e.g. 2020)' | t
                }}</mat-error>
                }
              </mat-form-field>
              }
              <abb-address-field
                (isAddressValid)="enableFields($event)"
                [showMap]="true"
                [mode]="mode()"
              />
            </div>
          </div>

          } @else if (index === 1){
          <div
            formGroupName="contactsForm"
            class="flex flex-col gap-6 w-full max-w-full"
          >
            <mat-form-field class="w-full" color="primary">
              <mat-label>{{ 'Select Users' | t }}</mat-label>
              <mat-select
                id="contacts"
                formControlName="contacts"
                multiple
                [panelClass]="'panel border-font-checkbox'"
              >
                @for (contact of contacts$ | async; track contact.id) {
                <mat-option
                  [id]="'contacts-' + contact.id"
                  [value]="contact.id"
                >
                  {{ contact.name }} {{ contact.lastName }}
                  <small>({{ contact.email }})</small>
                </mat-option>
                }
              </mat-select>
              @if(form.controls.contactsForm.get('contacts')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            <!-- 
            ============================================================
            TEMPORARILY DISABLED: Add Key Contact Feature
            ============================================================
            
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Add Key Contact' | t }}
            </h3>

            <div class="flex flex-row w-full gap-3">
              <div class="flex flex-col w-full" formGroupName="user">
                @if(form.controls.contactsForm.controls.user; as userForm) {
                <div class="flex flex-col gap-3">
                  <mat-form-field class="w-full" color="primary">
                    <mat-label>{{ 'Name' | t }}</mat-label>
                    <input
                      matInput
                      id="name"
                      formControlName="name"
                      name="name"
                    />
                  </mat-form-field>

                  <mat-form-field class="w-full" color="primary">
                    <mat-label>{{ 'Surname' | t }}</mat-label>
                    <input
                      matInput
                      id="lastName"
                      formControlName="lastName"
                      name="lastName"
                    />
                  </mat-form-field>

                  <mat-form-field class="w-full" color="primary">
                    <mat-label>{{ 'Email' | t }}</mat-label>
                    <input
                      matInput
                      id="email"
                      formControlName="email"
                      name="email"
                    />
                    @if(userForm.get('email')?.hasError('email') ||
                    userForm.get('email')?.hasError('pattern')){
                    <mat-error>{{
                      'Email is not valid (e.g., user@example.com)' | t
                    }}</mat-error>
                    }
                  </mat-form-field>

                  <mat-form-field class="w-full" color="primary">
                    <mat-label>{{ 'Role' | t }}</mat-label>
                    <mat-select
                      id="role"
                      formControlName="role"
                      name="role"
                      [panelClass]="'panel'"
                    >
                      @for (option of options; track option) {
                      <mat-option
                        [id]="'role-' + option.value"
                        [value]="option.value"
                        >{{ option.label }}</mat-option
                      >
                      }
                    </mat-select>
                    @if(form.get('role')?.hasError('required')){
                    <mat-error>{{ 'This field is required' | t }}</mat-error>
                    }
                  </mat-form-field>
                </div>
                }
              </div>
            </div>

            <button
              type="button"
              id="property-form-add-key-contact"
              [ngClass]="
                userFormRequiredFieldsError() ||
                form.controls.contactsForm.controls.user.invalid
                  ? 'text-abb-primary-light'
                  : 'text-abb-primary'
              "
              class="flex items-center gap-x-1 font-medium justify-end"
              [disabled]="
                userFormRequiredFieldsError() ||
                form.controls.contactsForm.controls.user.invalid
              "
              (click)="addKeyContact()"
            >
              <abb-icon
                size="medium"
                name="mat-outlined/library_add"
              ></abb-icon>
              {{ 'Add Key Contact' | t }}
            </button>
            ============================================================
            END TEMPORARILY DISABLED SECTION
            ============================================================
            -->
          </div>
          } @else if (index === 2 && this.mode() == 'edit') {
          <div
            formGroupName="consumptionForm"
            class="flex flex-col gap-6 w-full max-w-full"
          >
            <mat-form-field class="w-full" color="primary">
              <mat-label>{{ 'Devices' | t }}</mat-label>
              <mat-select
                id="consumptionSources"
                formControlName="consumptionSources"
                multiple
                [panelClass]="'panel border-font-checkbox'"
              >
                @for (device of propertyDevices$ | async; track device.id) {
                <mat-option [id]="device.id" [value]="device.id">
                  {{ device.name }}
                </mat-option>
                }
              </mat-select>
              @if(form.controls.contactsForm.get('contacts')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
          </div>
          }
        </form>
      </ng-template>
      <ng-template #lastStepBtn>
        <abb-button
          class="w-32"
          id="property-form-submit"
          [type]="'submit'"
          form="property-form"
          [loading]="isLoading()"
          [disabled]="!form.controls.propertyInfo.valid || isLoading()"
          >{{ (mode() === 'new' ? 'Create' : 'Save') | t }}</abb-button
        >
      </ng-template>
    </abb-stepper>
    } @else {
    <form
      id="property-form"
      class="flex flex-col gap-3 min-w-[448.5px]"
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      #propertyFormRef="ngForm"
    >
      @if(!showContacts()) {
      <div
        [formGroup]="form.controls.propertyInfo"
        formGroupName="propertyInfo"
        class="flex flex-row w-full gap-3 pt-6"
      >
        <div class="flex flex-col w-full gap-3">
          <mat-form-field class="w-full" color="primary">
            <mat-label>{{ 'Name' | t }}</mat-label>
            <input id="name" matInput formControlName="name" name="name" />
            @if(form.controls.propertyInfo.get('name')?.hasError('required')){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
          <mat-form-field class="w-full" color="primary">
            <mat-label>{{ 'Timezone' | t }}</mat-label>
            <mat-select
              formControlName="timezone"
              id="timezone"
              name="timezone"
              [panelClass]="'panel'"
            >
              @if(countryDetails() | async; as countryDetails){ @for (timezone
              of countryDetails.time_zones; track timezone?.id) {
              <mat-option [id]="timezone?.id ?? ''" [value]="timezone?.id">
                {{ timezone?.zone }}
                <small>({{ timezone?.utc_offset }})</small>
              </mat-option>
              }}
            </mat-select>
            @if(!this.showAddressHint()){
            <mat-hint class="text-abb-font/65"
              ><small>{{ 'Choose an address first' | t }}</small></mat-hint
            >
            }
            @if(form.controls.propertyInfo.get('timezone')?.hasError('required')){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
          <mat-form-field class="w-full" color="primary">
            <mat-label>{{ 'Currency' | t }}</mat-label>
            <mat-select
              formControlName="currency"
              id="currency"
              name="currency"
              [panelClass]="'panel'"
            >
              @if(countryDetails() | async; as countryDetails) { @for (currency
              of countryDetails.currencies; track currency?.id) {
              <mat-option [id]="currency?.id ?? ''" [value]="currency?.id">
                {{ currency?.value }}
              </mat-option>
              } }
            </mat-select>
            @if(!this.showAddressHint()){
            <mat-hint class="text-abb-font/65"
              ><small>{{ 'Choose an address first' | t }}</small></mat-hint
            >
            }
            @if(form.controls.propertyInfo.get('currency')?.hasError('required')){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            }
          </mat-form-field>
          <div class="image-upload-container">
            @if(imagePreview){
            <div class="image-preview">
              <img
                [src]="imagePreview"
                alt="Image Preview"
                class="preview-image"
              />
            </div>
            }

            <label for="imageUpload" class="flex image-upload-label"
              ><abb-icon
                class="mr-2"
                [name]="'mat/image'"
                [externalSize]="'text-lg'"
              ></abb-icon
              >{{ 'Choose an Image' | t }}</label
            >
            <input
              type="file"
              id="imageUpload"
              (change)="onFileSelected($event)"
              accept="image/*"
            />
          </div>
        </div>

        <abb-address-field
          (isAddressValid)="enableFields($event)"
          [showMap]="true"
          [mode]="mode()"
        />
      </div>
      <abb-button
        variant="secondary"
        type="button"
        class="flex items-center gap-x-1 font-medium justify-end py-1 gap-1"
        (onclick)="showContacts.set(true)"
        >{{ 'Add Contacts' | t }}</abb-button
      >
      } @else {
      <!-- FORM CONTATTI -->
      <div
        formGroupName="contactsForm"
        class="flex flex-col gap-3 w-full max-w-full"
        [ngClass]="{ 'pb-6': !showContacts() }"
      >
        <h3 class="py-2 text-lg font-semibold text-abb-font/85">
          {{ 'Contacts' | t }}
        </h3>

        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Select Users' | t }}</mat-label>
          <mat-select
            id="contacts"
            formControlName="contacts"
            multiple
            [panelClass]="'panel border-font-checkbox'"
          >
            @for (contact of contacts$ | async; track contact.id) {
            <mat-option [id]="'contacts-' + contact.id" [value]="contact.id">
              {{ contact.name }} {{ contact.lastName }}
              <small>({{ contact.email }})</small>
            </mat-option>
            }
          </mat-select>
          @if(form.controls.contactsForm.get('contacts')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>
        <!-- 
        ============================================================
        TEMPORARILY DISABLED: Add Key Contact Feature
        ============================================================
        
        <div class="w-full flex flex-col gap-3">
          <h3 class="text-base font-semibold text-abb-font/85">
            {{ 'Add Key Contact' | t }}
          </h3>

          <div class="flex flex-row w-full gap-3">
            <div class="flex flex-col w-full" formGroupName="user">
              @if(form.controls.contactsForm.controls.user; as userForm) {
              <div class="flex flex-col gap-3">
                <mat-form-field class="w-full" color="primary">
                  <mat-label>{{ 'Name' | t }}</mat-label>
                  <input
                    matInput
                    id="name"
                    formControlName="name"
                    name="name"
                  />
                </mat-form-field>

                <mat-form-field class="w-full" color="primary">
                  <mat-label>{{ 'Surname' | t }}</mat-label>
                  <input
                    matInput
                    id="lastName"
                    formControlName="lastName"
                    name="lastName"
                  />
                </mat-form-field>

                <mat-form-field class="w-full" color="primary">
                  <mat-label>{{ 'Email' | t }}</mat-label>
                  <input
                    matInput
                    id="email"
                    formControlName="email"
                    name="name"
                  />
                  @if(userForm.get('email')?.hasError('email') ||
                  userForm.get('email')?.hasError('pattern')){
                  <mat-error>{{
                    'Email is not valid (e.g., user@example.com)' | t
                  }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field class="w-full" color="primary">
                  <mat-label>{{ 'Role' | t }}</mat-label>
                  <mat-select
                    id="role"
                    formControlName="role"
                    name="role"
                    [panelClass]="'panel'"
                  >
                    @for (option of options; track option) {
                    <mat-option
                      [id]="'role-' + option.value"
                      [value]="option.value"
                      >{{ option.label }}</mat-option
                    >
                    }
                  </mat-select>
                </mat-form-field>
              </div>
              }
            </div>
          </div>
          <button
            type="button"
            id="property-form-add-key-contact"
            [ngClass]="
              userFormRequiredFieldsError() ||
              form.controls.contactsForm.controls.user.invalid
                ? 'text-abb-primary-light'
                : 'text-abb-primary'
            "
            class="flex items-center gap-x-1 font-medium justify-end"
            [disabled]="
              userFormRequiredFieldsError() ||
              form.controls.contactsForm.controls.user.invalid
            "
            (click)="addKeyContact()"
          >
            <abb-icon size="medium" name="mat-outlined/library_add"></abb-icon>
            {{ 'Add Key Contact' | t }}
          </button>
        </div>
        ============================================================
        END TEMPORARILY DISABLED SECTION
        ============================================================
        -->
      </div>
      }
    </form>
    }
  `,
  styles: `
    .image-upload-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border: 1px dashed rgb(var(--abb-color-black) / 0.15);
      border-radius: 8px;
      width: 100%;
      height: 100%;
      min-height: 200px;
      text-align: center;
      cursor: pointer;
    }

    .image-upload-label {
      border: 1px solid rgb(var(--abb-color-black) / 0.15);
      color: rgb(var(--abb-color-black) / 0.30);
      align-items: center;
      justify-content: center;
      padding: 10px;
      border-radius: 4px;
      cursor: pointer;
    }

    input[type="file"] {
      display: none;
    }

    .image-preview {
      margin-bottom: 1rem;
      max-height: 130px;
      aspect-ratio: 1;
    }

    .preview-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: cover;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

  .form-field {
    --mat-mdc-form-field-floating-label-scale: 0.75;
    display: inline-flex;
    flex-direction: column;
    min-width: 0;
    text-align: left;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    font-family: var(--mat-form-field-container-text-font);
    line-height: var(--mat-form-field-container-text-line-height);
    font-size: var(--mat-form-field-container-text-size);
    letter-spacing: var(--mat-form-field-container-text-tracking);
    font-weight: var(--mat-form-field-container-text-weight);
  }

  .form-field-wrapper, .form-field-bottom-align::before {
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    font-family: var(--mat-form-field-subscript-text-font);
    line-height: var(--mat-form-field-subscript-text-line-height);
    font-size: var(--mat-form-field-subscript-text-size);
    letter-spacing: var(--mat-form-field-subscript-text-tracking);
    font-weight: var(--mat-form-field-subscript-text-weight);
  }

  .form-field-bottom-align::before {
    content: "";
    display: inline-block;
    height: 32px;
  }

  .form-field-wrapper {
    box-sizing: border-box;
    width: 100%;
    position: relative;
  }

  .form-field-error-wrapper {
    opacity: 1;
    transform: translateY(0%);
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0 16px;
  }
  `,
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'properties' }],
})
export class PropertyFormComponent implements OnInit, OnDestroy {
  updateLTValidators(isLT: boolean) {
    const propertyInfo = this.form.controls.propertyInfo;
    propertyInfo
      .get('heatingEnergyConsumption')
      ?.setValidators(
        isLT ? [Validators.required, Validators.min(0)] : [Validators.min(0)]
      );
    propertyInfo.get('heatingEnergyConsumption')?.updateValueAndValidity();
    propertyInfo
      .get('electricalEnergyConsumption')
      ?.setValidators(
        isLT ? [Validators.required, Validators.min(0)] : [Validators.min(0)]
      );
    propertyInfo.get('electricalEnergyConsumption')?.updateValueAndValidity();
    propertyInfo.get('constructionYear')?.setValidators([
      (control) => {
        const value = control.value;
        if (value === null || value === undefined || value === '') return null;
        const currentYear = new Date().getFullYear();
        if (!/^\d{4}$/.test(String(value))) {
          return { yearFormat: true };
        }
        const year = Number(value);
        if (year < 1000 || year > currentYear) {
          return { yearFormat: true };
        }
        return null;
      },
      ...(isLT ? [Validators.required] : []),
    ]);
    propertyInfo.get('constructionYear')?.updateValueAndValidity();
    propertyInfo
      .get('heatedFloorArea')
      ?.setValidators(
        isLT ? [Validators.required, Validators.min(0)] : [Validators.min(0)]
      );
    propertyInfo.get('heatedFloorArea')?.updateValueAndValidity();
    propertyInfo
      .get('heatingSystemType')
      ?.setValidators(isLT ? [Validators.required] : []);
    propertyInfo.get('heatingSystemType')?.updateValueAndValidity();
    propertyInfo
      .get('category')
      ?.setValidators(isLT ? [Validators.required] : []);
    propertyInfo.get('category')?.updateValueAndValidity();
  }
  prefix = inject(TRANSLATION_PREFIX);
  stepperView = input<boolean>(true);
  showContacts = signal<boolean>(false);
  isLoading = input<boolean>(false);
  mode = input.required<'new' | 'edit'>();
  initValues = input<Omit<
    NonNullable<PropertyDetailsQuery['propertyDetails']>,
    'status' | 'actualCosts' | 'todaysConsumption' | 'thisMonthsConsumption'
  > | null>();
  formValidationStatusChange = output<boolean>();
  formSubmit = output<any>();
  onCancel = output<void>();
  imagePreview: string | ArrayBuffer | null = null;
  imageAvatarPreview: string | ArrayBuffer | null = null;
  form = new FormGroup({
    propertyInfo: new FormGroup({
      name: new FormControl<string>('', [Validators.required]),
      fullAddress: new FormControl('', [Validators.required]),
      country: new FormControl<string>('', []),
      city: new FormControl<string>('', []),
      postalCode: new FormControl<string>('', []),
      address: new FormControl<string>('', []),
      coordinates: new FormControl<Coordinates>({ lat: 0, lng: 0 }),
      image: new FormControl<string | ArrayBuffer | null>('image', [
        Validators.required,
      ]),
      timezone: new FormControl<string>('', [Validators.required]),
      currency: new FormControl<string>('', [Validators.required]),
      heatedFloorArea: new FormControl<number | null>(null, [
        Validators.min(0),
      ]),
      heatingSystemType: new FormControl<string | null>(null, []),
      heatingEnergyConsumption: new FormControl<number | null>(null, [
        Validators.min(0),
      ]),
      constructionYear: new FormControl<number | null>(null, [
        Validators.min(1800),
      ]),
      category: new FormControl<string | null>(null, []),
      electricalEnergyConsumption: new FormControl<number | null>(null, [
        Validators.min(0),
      ]),
    }),
    contactsForm: new FormGroup({
      contacts: new FormControl<string[] | null>([], []),
      user: new FormGroup({
        name: new FormControl<string | null>(null),
        lastName: new FormControl<string | null>(null),
        // TEMPORARILY DISABLED: email validators removed for Add Key Contact feature
        email: new FormControl<string | null>(null, [
          // Validators.email,
          // Validators.pattern(
          //   /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
          // ),
        ]),
        role: new FormControl<string | null>(null),
      }),
    }),
    consumptionForm: new FormGroup({
      consumptionSources: new FormControl<string[] | null>([], []),
    }),
  });
  // TEMPORARILY DISABLED: userFormRequiredFieldsError signal for Add Key Contact feature
  // userFormRequiredFieldsError = signal(true);
  // SIGNALS FOR LICENSE LOGIC
  licenseId = signal<string>('');
  isLTLicense = signal<boolean>(false);
  @ViewChild('propertyFormRef') propertyFormRef!: NgForm;
  stepsControls = computed(() => {
    const baseSteps: FormGroup[] = [
      this.form.controls.propertyInfo,
      this.form.controls.contactsForm,
    ];

    if (this.mode() === 'edit') {
      baseSteps.push(this.form.controls.consumptionForm);
    }

    return baseSteps;
  });
  stepsLabels = computed(() => {
    const baseLabels = ['', t(this.prefix, 'Contacts', this.translate)];

    if (this.mode() === 'edit') {
      baseLabels.push(t(this.prefix, 'Consumption', this.translate));
    }

    return baseLabels;
  });
  contacts$ = new BehaviorSubject<PartialUser[]>([]);
  propertyDevices$ = new BehaviorSubject<
    PropertyDevicesQuery['propertyDevices']
  >([]);
  loading = signal(true);
  countryDetails = signal<
    BehaviorSubject<GetCountryDetailsQuery['getCountryDetails']>
  >({} as BehaviorSubject<GetCountryDetailsQuery['getCountryDetails']>);
  showAddressHint = signal<boolean>(false);

  private readonly subscriptions: Subscription[] = [];
  readonly options = Object.entries(ROLES).map(([key, value]) => {
    return {
      value: key,
      label: value,
    };
  });

  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toastService: ToastService,
    public countrydetailservice: CountryDetailsService
  ) {
    this.countryDetails.set(countrydetailservice.countryDetails);

    // TEMPORARILY REMOVED: Disable contactsForm validation for Add Key Contact feature
    // this.form.controls.contactsForm.disable();

    this.subscriptions.push(
      /* TEMPORARILY DISABLED: subscription for userFormRequiredFieldsError (Add Key Contact feature)
      this.form.controls.contactsForm.controls.user.valueChanges.subscribe(
        (v) => {
          const required = [
            v.email?.trim(),
            v.lastName?.trim(),
            v.name?.trim(),
            v.role,
          ];
          this.userFormRequiredFieldsError.set(
            required?.some((f) => !f || f === '')
          );
        }
      ),
      */
      this.form.statusChanges.subscribe(() => {
        const isPropertyInfoValid = this.form.controls.propertyInfo.valid;
        this.formValidationStatusChange.emit(!isPropertyInfoValid);
      }),
      apollo
        .watchQuery<PropertyDetailContactsQuery>({
          query: propertyDetailContactsQuery,
        })
        .valueChanges.subscribe({
          next: (result) => {
            const contacts = result.data.userList.reduce(
              (acc, curr) => [
                ...acc,
                {
                  id: curr.id,
                  name: curr.name,
                  lastName: curr.lastName,
                  email: curr.email,
                  role: curr.role,
                },
              ],
              [] as PartialUser[]
            );
            this.contacts$.next(contacts);
          },
          error: (err) => {
            console.error('Error:', err);
          },
        })
    );
  }

  ngOnInit() {
    if (this.mode() == 'edit') {
      this.imagePreview = this.initValues()?.imgUrl ?? '';
      this.form.controls.propertyInfo.patchValue({
        name: this.initValues()?.name ?? '',
        country: this.initValues()?.country ?? '',
        city: this.initValues()?.city ?? '',
        postalCode: this.initValues()?.postalCode ?? '',
        address: this.initValues()?.address ?? '',
        fullAddress: this.initValues()?.fullAddress ?? '',
        coordinates: {
          lat: this.initValues()?.coordinates?.lat ?? 0,
          lng: this.initValues()?.coordinates?.lng ?? 0,
        },
        image: this.initValues()?.imgUrl ?? '',
        heatedFloorArea: this.initValues()?.heatedFloorArea ?? null,
        heatingSystemType: this.initValues()?.heatingSystemType ?? null,
        heatingEnergyConsumption:
          this.initValues()?.heatingEnergyConsumption ?? null,
        constructionYear: this.initValues()?.constructionYear ?? null,
        category: this.initValues()?.category ?? null,
        electricalEnergyConsumption:
          this.initValues()?.electricalEnergyConsumption ?? null,
      });
      this.form.controls.consumptionForm.patchValue({
        consumptionSources: this.initValues()?.consumptionSources ?? [],
      });
      this.subscriptions.push(
        combineLatest({
          contacts: this.contacts$.asObservable(),
        })
          .pipe(debounceTime(400))
          .subscribe((result) => {
            const contactsIds = result.contacts
              .filter((contact) =>
                this.initValues()
                  ?.contacts?.map((c) => c.id)
                  .includes(contact.id)
              )
              ?.map((c) => c.id);
            this.form.controls.contactsForm.controls.contacts.setValue(
              contactsIds ?? []
            );
          })
      );

      // LOGICA PER OTTENERE LICENSE ID E isLTLicense
      // 1. Ottieni licenseId tramite query getLicenseId
      this.apollo
        .watchQuery<any>({
          query: getLicenseIdQuery,
        })
        .valueChanges.subscribe({
          next: (result) => {
            const licenseId = result.data?.getLicenseId ?? '';
            this.licenseId.set(licenseId);
            if (licenseId) {
              // 2. Usa licenseId per ottenere isLTLicense
              this.apollo
                .watchQuery<any>({
                  query: propertyLicenseTypeQuery,
                  variables: { id: licenseId },
                })
                .valueChanges.subscribe({
                  next: (result2) => {
                    const isLT =
                      result2.data?.propertyLicenseType?.isLTLicense ?? false;
                    this.isLTLicense.set(isLT);
                    this.updateLTValidators(isLT);
                  },
                  error: (err2) => {
                    console.error('Error fetching isLTLicense:', err2);
                    this.isLTLicense.set(false);
                    this.updateLTValidators(false);
                  },
                });
            } else {
              this.updateLTValidators(false);
            }
          },
          error: (err) => {
            console.error('Error fetching licenseId:', err);
            this.licenseId.set('');
            this.updateLTValidators(false);
          },
        });
    } else {
      this.form.controls.propertyInfo.get('timezone')?.disable();
      this.form.controls.propertyInfo.get('currency')?.disable();
    }
  }
  ngOnChanges() {
    if (this.mode() == 'edit') {
      this.apollo
        .watchQuery<PropertyDevicesQuery>({
          query: propertyDeviceQuery,
          variables: { id: this.initValues()?.id! },
        })
        .valueChanges.subscribe({
          next: (result) => {
            const devices = result.data.propertyDevices.filter(
              (d) =>
                d.category.name === 'THIRD_PARTY_RTU' ||
                d.category.name === 'THIRD_PARTY_TCP' ||
                d.category.name === 'ENERGY_METER' ||
                d.category.name === 'CURRENT_SENSOR'
            );
            this.propertyDevices$.next(devices);
          },
          error: (err) => {
            console.error('Error:', err);
          },
        });
    }
  }
  enableFields(event: boolean) {
    this.showAddressHint.set(event);
    if (!event) {
      this.form.controls.propertyInfo.get('timezone')?.disable();
      this.form.controls.propertyInfo.get('currency')?.disable();
      this.form.controls.propertyInfo.get('timezone')?.setValue('');
      this.form.controls.propertyInfo.get('currency')?.setValue('');
    } else {
      this.countryDetails().subscribe((details) => {
        const { time_zones, currencies } = details;

        const timezone = time_zones
          ?.filter(Boolean)
          ?.map((c) => c!.id)
          ?.find((t) => t === this.initValues()?.timezone);

        const currency = currencies
          ?.filter(Boolean)
          ?.map((c) => c!.id)
          ?.find((c) => c === this.initValues()?.currency);

        this.form.controls.propertyInfo.patchValue({
          timezone: timezone ?? '',
          currency: currency ?? '',
        });

        if (time_zones?.length === 1) {
          this.form.controls.propertyInfo
            .get('timezone')
            ?.setValue(time_zones[0]?.id ?? '');
          this.form.controls.propertyInfo.get('timezone')?.disable();
        } else {
          this.form.controls.propertyInfo.get('timezone')?.enable();
        }

        if (currencies?.length === 1) {
          this.form.controls.propertyInfo
            .get('currency')
            ?.setValue(currencies[0]?.id ?? '');
          this.form.controls.propertyInfo.get('currency')?.disable();
        } else {
          this.form.controls.propertyInfo.get('currency')?.enable();
        }
      });
    }
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    const maxSize = 1 * 1024 * 1024; // 1 MB limit, adjust as needed
    if (file) {
      if (file.size > maxSize) {
        this.toastService.notifyError(
          'File size exceeds the maximum limit of 1 MB.'
        );
        return;
      }
      this.form.controls.propertyInfo.get('image')?.updateValueAndValidity();
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
        this.form.patchValue({ propertyInfo: { image: this.imagePreview } });
      };
      reader.readAsDataURL(file);
    }
  }

  saving = signal(false);

  /* 
  ============================================================
  TEMPORARILY DISABLED: addKeyContact() method
  ============================================================
  This method handles adding new key contacts directly from the form.
  Temporarily commented out as requested - will be re-enabled later.
  
  addKeyContact() {
    const userForm = this.form.controls.contactsForm.controls.user;
    this.saving.set(true);
    const formValue = userForm.value;
    const requiredFields = {
      name: formValue.name?.trim(),
      lastName: formValue.lastName?.trim(),
      email: formValue.email?.trim(),
      role: formValue.role,
    };
    this.apollo
      .watchQuery<CanCreateUserQuery, CanCreateUserQueryVariables>({
        query: canCreateUserQuery,
        variables: { email: userForm.value.email! },
      })
      .valueChanges.subscribe({
        next: ({ data }) => {
          if (data.canCreateUser && data.canCreateUser.canCreate === true) {
            this.apollo
              .mutate<CreateUserMutation, CreateUserMutationVariables>({
                mutation: createUserMutation,
                variables: {
                  name: requiredFields.name!,
                  lastName: requiredFields.lastName!,
                  email: requiredFields.email!,
                  role: requiredFields.role! as Roles,
                  propertyIds: [this.initValues()?.id ?? ''],
                },
              })
              .subscribe({
                next: ({ data }) => {
                  this.saving.set(false);
                  this.toastService.notifySuccess(
                    t(
                      this.prefix,
                      `{name} was created successfully`,
                      this.translate,
                      {
                        name:
                          userForm.value.name + ' ' + userForm.value.lastName,
                      }
                    )
                  );
                  this.apollo.client
                    .refetchQueries({
                      include: ['PropertyDetailContacts'],
                    })
                    .then(() => {
                      const contactsForm =
                        this.form.controls.contactsForm.controls.contacts;
                      contactsForm.patchValue([
                        ...(contactsForm.value || []),
                        data?.createUser?.id!,
                      ]);
                    });
                  userForm.reset();
                },
                error: (err) => {
                  this.saving.set(false);
                  console.error('Create user error:', err);
                  // let errMsg = getErrorMessage(err, this.translate);
                  // this.toastService.notifyError(errMsg);
                  userForm.reset();
                },
              });
          } else {
            this.saving.set(false);
            this.toastService.notifyError(
              t(
                this.prefix,
                data.canCreateUser?.reason ??
                  'User with this email already exists',
                this.translate
              )
            );
          }
        },
      });
  }
  ============================================================
  END TEMPORARILY DISABLED METHOD
  ============================================================
  */

  onSubmit(): void {
    const isLT = this.isLTLicense();
    const formValue = this.form.value;
    const propertyInfo: any = { ...formValue.propertyInfo };

    const countryDetailsValue = this.countryDetails().getValue?.();
    const selectedTimezoneId =
      this.form.controls.propertyInfo.get('timezone')?.value ?? '';
    const selectedCurrencyId =
      this.form.controls.propertyInfo.get('currency')?.value ?? '';
    propertyInfo.timezone =
      countryDetailsValue?.time_zones?.find(
        (tz) => tz?.id === selectedTimezoneId
      )?.zone ?? selectedTimezoneId;
    propertyInfo.currency =
      countryDetailsValue?.currencies?.find(
        (cur) => cur?.id === selectedCurrencyId
      )?.value ?? selectedCurrencyId;

    if (!isLT) {
      [
        'heatedFloorArea',
        'heatingSystemType',
        'heatingEnergyConsumption',
        'constructionYear',
        'category',
        'electricalEnergyConsumption',
      ].forEach((field) => {
        delete propertyInfo[field];
      });
    }

    propertyInfo.isLTProperty = isLT;

    const newForm = {
      ...this.form.value,
      propertyInfo,
    };
    console.log('Payload inviato:', newForm);
    this.formSubmit.emit(newForm);
  }

  cancel() {
    this.onCancel.emit();
  }

  submit() {
    this.propertyFormRef.ngSubmit.emit();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  showError(e: any) {
    if (e.containsRequired) {
      this.toastService.notifyError(
        t(
          this.prefix,
          `Please fill in all required fields before moving forward`,
          this.translate
        )
      );
    } else if (e.containsInvalid) {
      this.toastService.notifyError(
        t(this.prefix, `Some fields are not valid`, this.translate)
      );
    }
  }
}
